import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { catchError, EMPTY, map, Observable, take, tap } from 'rxjs';
import { OmniSearchResult } from '@brighthub/search';
import {
  ClientSessionService,
  SingleUiSessionsResponse,
  UiSession,
  UiSessionsResponse,
} from '@brighthub/uisession';
import { ApiService, GenericResponseCode } from '@brighthub/api';

@Injectable({
  providedIn: 'root',
})
export class ConversationService implements OnDestroy {
  private _uiSessionService = inject(ClientSessionService);
  activeConversation = signal<UiSession | null>(null);
  conversationHistory = signal<UiSession[] | []>([]);
  private _tabId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  private _channel = new BroadcastChannel('brightnav-conversations');

  constructor() {
    this._channel.onmessage = (event: MessageEvent) => {
      const { type, payload, meta } = event.data ?? {};
      if (!type) return;
      if (meta?.sender === this._tabId) return;

      switch (type) {
        case 'requestConversationHistory':
          this._broadcastHistory(this.conversationHistory(), meta?.sender);
          break;
        case 'updateConversationHistory':
          if (!Array.isArray(payload)) return;
          if (meta?.recipient && meta.recipient !== this._tabId) return;
          this._applyRemoteHistory(payload as UiSession[]);
          break;
      }
    };

    this._channel.postMessage({
      type: 'requestConversationHistory',
      meta: { sender: this._tabId },
    });
  }

  ngOnDestroy(): void {
    this._channel.close();
  }

  private _applyRemoteHistory(remote: UiSession[]) {
    const current = this.conversationHistory();
    const byId = new Map(current.map((s) => [s.conversationId, s]));

    const merged: UiSession[] = [];
    for (const s of remote) {
      if (byId.has(s.conversationId)) {
        merged.push(byId.get(s.conversationId)!);
      } else {
        merged.push(s);
      }
    }

    for (const s of current) {
      if (!merged.some((m) => m.conversationId === s.conversationId)) {
        merged.push(s);
      }
    }

    if (!this._sameHistory(current, merged)) {
      this.conversationHistory.set(merged);
    }
  }

  private _sameHistory(current: UiSession[], next: UiSession[]): boolean {
    // Conversation history is rendered in this order, so a reorder is a real
    // state change even when both arrays contain the same conversations.
    return (
      current.length === next.length &&
      current.every(
        (session, index) => session.uiSessionId === next[index].uiSessionId,
      )
    );
  }

  private _broadcastHistory(history: UiSession[], recipient?: string): void {
    try {
      this._channel.postMessage({
        type: 'updateConversationHistory',
        payload: history,
        meta: { sender: this._tabId, recipient },
      });
    } catch (err) {
      console.warn('Failed to post conversationHistory', err);
    }
  }

  queryConversationById(
    conversationId: string,
  ): Observable<OmniSearchResult<UiSession>[]> {
    return this._uiSessionService
      .getUiSession<SingleUiSessionsResponse>(`/conversation/${conversationId}`)
      .pipe(
        map((res) => {
          if (res.result.code === GenericResponseCode.SERVICE_OK) {
            const results = {
              description: res.payload.uiSession.uiSessionId,
              title: res.payload.uiSession.conversationId!,
              data: res.payload.uiSession,
            };

            return [results];
          }
          return [];
        }),
      );
  }

  getConversationById(conversationId: string) {
    this._uiSessionService
      .getUiSession<SingleUiSessionsResponse>(`/conversation/${conversationId}`)
      .pipe(
        take(1),
        tap((res) => {
          if (res.result.code === GenericResponseCode.SERVICE_OK) {
            const session = res.payload.uiSession;
            this.setActiveConversation(session);
          } else {
            // do something here
          }
        }),
        catchError((err) => {
          return EMPTY;
        }),
      )
      .subscribe();
  }

  setActiveConversation(session: UiSession) {
    this.activeConversation.set(session);
    this._addToHistory(session);
  }

  private _addToHistory(session: UiSession) {
    const current = this.conversationHistory();
    const exists = current.some(
      (c) => c.conversationId === session.conversationId,
    );

    if (!exists) {
      const history = [session, ...current];
      this.conversationHistory.set(history);
      this._broadcastHistory(history);
    }
  }

  clearActiveConversation() {
    this.activeConversation.set(null);
  }
}
